import {
  useCreateRecurringWorkOrder,
  useDeleteRecurringWorkOrder,
  useListRecurringWorkOrders,
  useUpdateRecurringWorkOrder,
  type RecurringWorkOrderIn,
  type RecurringWorkOrderOut,
} from '@myrentalmate/api-client';
import { Chip } from '@mui/material';

import {
  optional,
  RecordPage,
  required,
  type Column,
  type Field,
  type FormValues,
  type ListParams,
} from '../../components/RecordPage';
import { listResult } from '../../lib/apiData';
import { formatDate } from '../../lib/format';
import { useCan } from '../../lib/permissions';
import { CATEGORY, optionsOf, PRIORITY } from './labels';
import {
  assigneeValue,
  parseAssignee,
  useAssigneePicker,
  useWherePicker,
  whereValue,
} from './usePickers';

const useRows = ({ q, page, pageSize }: ListParams) =>
  listResult(useListRecurringWorkOrders({ q, page, page_size: pageSize }));

const every = (months: number) =>
  months === 1 ? 'Every month' : months === 12 ? 'Every year' : `Every ${months} months`;

const COLUMNS: Column<RecurringWorkOrderOut>[] = [
  { label: 'Work', render: (r) => r.title },
  { label: 'Where', render: (r) => r.place },
  { label: 'How often', render: (r) => every(r.interval_months) },
  { label: 'Next due', render: (r) => formatDate(r.next_due_on) },
  { label: 'Assigned to', render: (r) => r.assignee_name ?? '–' },
  {
    label: 'Status',
    render: (r) =>
      r.is_active ? (
        <Chip size="small" color="success" label="Active" />
      ) : (
        <Chip size="small" label="Paused" />
      ),
  },
];

export function RecurringPage() {
  const canManage = useCan('maintenance.manage');
  const create = useCreateRecurringWorkOrder();
  const update = useUpdateRecurringWorkOrder();
  const remove = useDeleteRecurringWorkOrder();
  const where = useWherePicker();
  const assignees = useAssigneePicker();

  const fields: Field[] = [
    { name: 'title', label: 'What Needs Doing', required: true },
    { name: 'where', label: 'Where', type: 'select', required: true, options: where.options },
    {
      name: 'interval_months',
      label: 'Every How Many Months',
      type: 'number',
      required: true,
      defaultValue: '6',
      helperText: '1 to 60',
    },
    {
      name: 'next_due_on',
      label: 'Next Due',
      type: 'date',
      required: true,
      helperText: 'The work order appears a week before this',
    },
    {
      name: 'category',
      label: 'Type',
      type: 'select',
      required: true,
      defaultValue: 'general',
      options: optionsOf(CATEGORY),
    },
    {
      name: 'priority',
      label: 'Priority',
      type: 'select',
      required: true,
      defaultValue: 'normal',
      options: optionsOf(PRIORITY),
    },
    { name: 'assignee', label: 'Assigned To', type: 'select', options: assignees },
    { name: 'description', label: 'Details', type: 'multiline' },
    { name: 'is_active', label: 'Active', type: 'checkbox', defaultValue: true },
  ];

  function toBody(values: FormValues): RecurringWorkOrderIn {
    return {
      ...where.parse(required(values, 'where')),
      ...parseAssignee(String(values.assignee ?? '')),
      title: required(values, 'title'),
      interval_months: Number(required(values, 'interval_months')),
      next_due_on: required(values, 'next_due_on'),
      category: required(values, 'category') as RecurringWorkOrderIn['category'],
      priority: required(values, 'priority') as RecurringWorkOrderIn['priority'],
      description: optional(values, 'description'),
      is_active: Boolean(values.is_active),
    };
  }

  return (
    <RecordPage
      title="Recurring Work"
      singular="schedule"
      description="Work that comes round again, like a furnace service. Each one becomes a work order a week ahead."
      fields={fields}
      columns={COLUMNS}
      canManage={canManage}
      useRows={useRows}
      toValues={(r) => ({
        title: r.title,
        where: whereValue(r),
        interval_months: String(r.interval_months),
        next_due_on: r.next_due_on,
        category: r.category,
        priority: r.priority,
        assignee: assigneeValue(r),
        description: r.description ?? '',
        is_active: r.is_active,
      })}
      create={(values) => create.mutateAsync({ data: toBody(values) })}
      update={(recordId, values) =>
        update.mutateAsync({ recurringId: recordId, data: toBody(values) })
      }
      remove={(recordId) => remove.mutateAsync({ recurringId: recordId })}
    />
  );
}
